import { format, utcToZonedTime } from 'date-fns-tz';
import { useMemo } from 'react';
import { getButtonStylesWithAccentColor } from '../../layouts/layout.utils';
import deepmerge from 'deepmerge';

const testIds = [
  'addToCalendar',
  'getDirections',
  'eventName',
  'primaryBlock',
  'secondaryBlock',
  'descriptionBlock',
  'dressCodeblock',
  'mapBlock',
  'timeframe',
  'livestream',
] as const;

type TestIds = (typeof testIds)[number];

export type ScheduleListTestIds = {
  listItemTestIds: Record<TestIds, { prop: { 'data-testid': string }; value: string }>;
};

const _prefixTestId = (value: string | number) => `item-${value}`;

export const getItemTestIds = (id: string): ScheduleListTestIds => {
  const listItemTestIds = testIds.reduce(
    (acc, key) => {
      const testId = _prefixTestId(`${key}-${id}`);
      acc[key] = {
        prop: { 'data-testid': testId },
        value: testId,
      };
      return acc;
    },
    {} as ScheduleListTestIds['listItemTestIds'],
  );

  return {
    listItemTestIds,
  };
};

// Maybe consider generalizing this for wide usage
export const useTestIds = (id: string) => {
  return useMemo(() => {
    return getItemTestIds(id);
  }, [id]);
};

// get timezone abbr by timezone name
export const getTimeZoneAbbr = (timeInMillis: number | undefined, timeZone: string | undefined) => {
  const zonedDate = utcToZonedTime(timeInMillis ? new Date(timeInMillis) : new Date(), timeZone || 'America/Los_Angeles');
  return format(zonedDate, 'zzz', { timeZone });
};

export const getCalendarButtonProps = (accentColor?: string) => {
  if (accentColor) {
    return getButtonStylesWithAccentColor(accentColor);
  }
  return {
    borderColor: 'mono3',
    color: 'mono14',
    _hover: { backgroundColor: 'mono2', borderColor: 'mono3', color: 'mono14' },
    _active: { backgroundColor: 'mono3', borderColor: 'mono3', color: 'mono14' },
    _disabled: {
      backgroundColor: 'mono3',
      borderColor: 'mono3',
      color: 'mono8',
      cursor: 'not-allowed',
    },
  };
};

export const mergeVariantStyles = <T>(x: Readonly<T>, y: Readonly<T>): T => {
  return deepmerge<T>(x, y, {
    clone: true,
    arrayMerge: (_target, source) => source,
  });
};
